import { useEffect, useRef } from "react";

/**
 * Controlled lite Spotify track.
 * Props:
 *  - id (string) : Spotify track id
 *  - title (string)
 *  - coverUrl? (string)
 *  - playing (bool) : controlled "is this the active one?"
 *  - onPlayRequest () => void : tell parent we want to become active
 */
export default function LiteSpotifyTrack({ id, title, coverUrl, playing, onPlayRequest }) {
  const iframeRef = useRef(null);
  const embed = `https://open.spotify.com/embed/track/${id}?theme=0${playing ? "&autoplay=1" : ""}`;

  // If parent turns us off, no extra work needed — iframe is unmounted.

  return (
    <div className="overflow-hidden rounded-xl leading-[0]">
      {playing ? (
        <iframe
          ref={iframeRef}
          title={`spotify-${id}-${title}`}
          src={embed}
          width="100%"
          height="152"
          style={{ border: 0, backgroundColor: "transparent" }}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
      ) : (
        <button
          type="button"
          onClick={onPlayRequest}
          className="group flex w-full items-center gap-4 p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition"
          aria-label={`Load Spotify player for ${title}`}
        >
          {coverUrl ? (
            // keep this simple to avoid pulling SmartImg here; TopSongs already optimizes images elsewhere
            <img
              src={coverUrl}
              alt={title}
              className="h-16 w-16 rounded-lg object-cover border border-white/10"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-white/10" />
          )}
          <div className="flex-1 text-left">
            <div className="text-slate-200 text-sm line-clamp-1">{title}</div>
            <div className="text-slate-400 text-xs">Click to play</div>
          </div>
          <span className="text-sm opacity-80 group-hover:opacity-100">▶</span>
        </button>
      )}
    </div>
  );
}
